import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AISearch.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const SUGGESTIONS = [
  { label: '📊 Priorités du jour', text: "Quelles sont mes priorités du jour ?" },
  { label: '⚠️ Litiges en retard', text: "Combien de litiges sont en retard ? Liste-les." },
  { label: '📥 Leads non traités', text: "Combien de leads sont en attente depuis plus de 48h ?" },
  { label: '📈 Statistiques globales', text: "Donne-moi les statistiques globales du CRM." },
  { label: '🔧 Bilan atelier', text: "Quel est le bilan de charge de l'atelier pour aujourd'hui ?" },
  { label: '✉️ Rédiger un mail', text: "Aide-moi à rédiger un mail professionnel." },
];

// ─── Markdown rendering (from ChatPanel.js) ─────────────────────────────────

function formatInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function formatMessage(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('#### ')) return <h5 key={i} className="ai-md-h4">{formatInline(line.slice(5))}</h5>;
    if (line.startsWith('### ')) return <h4 key={i} className="ai-md-h3">{formatInline(line.slice(4))}</h4>;
    if (line.startsWith('## ')) return <h3 key={i} className="ai-md-h2">{formatInline(line.slice(3))}</h3>;
    if (line.match(/^[-•*]\s/)) return <li key={i} className="ai-md-li">{formatInline(line.slice(2))}</li>;
    if (line.match(/^\d+\.\s/)) return <li key={i} className="ai-md-li ai-md-ol">{formatInline(line.replace(/^\d+\.\s/, ''))}</li>;
    if (!line.trim()) return <br key={i} />;
    return <p key={i} className="ai-md-p">{formatInline(line)}</p>;
  });
}

// ─── Composant principal ─────────────────────────────────────────────────────

export default function AISearch({ user, setHeaderAction }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [activeTool, setActiveTool] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);

  // Pas de header action pour cette page
  useEffect(() => {
    if (setHeaderAction) setHeaderAction(null);
  }, [setHeaderAction]);

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamContent, scrollToBottom]);

  // Auto-resize textarea
  const adjustTextarea = () => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 150) + 'px';
    }
  };

  // ─── Envoi de message ───────────────────────────────────────────────────────

  const sendMessage = async (text) => {
    const content = (text || input).trim();
    if (!content || isStreaming) return;

    const userMsg = { role: 'user', content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsStreaming(true);
    setStreamContent('');
    setActiveTool(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Historique pour l'API (derniers 10 messages user/assistant seulement)
    const history = newMessages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-10);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: content, history }),
        signal: (abortRef.current = new AbortController()).signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);

            switch (event.type) {
              case 'token':
                accumulated += event.content;
                setStreamContent(accumulated);
                break;
              case 'tool_start':
                setActiveTool(event.name);
                break;
              case 'tool_done':
                setActiveTool(null);
                break;
              case 'full':
                accumulated = event.content;
                setStreamContent(accumulated);
                break;
              case 'done':
                // Finaliser le message assistant
                if (accumulated) {
                  setMessages(prev => [...prev, { role: 'assistant', content: accumulated }]);
                }
                setStreamContent('');
                break;
              case 'error':
                accumulated = event.content || 'Une erreur est survenue.';
                setMessages(prev => [...prev, { role: 'assistant', content: accumulated }]);
                setStreamContent('');
                break;
              default:
                break;
            }
          } catch (e) {
            // Ignorer les lignes JSON invalides
          }
        }
      }

      // Si le stream s'est terminé sans event "done" explicite
      if (accumulated && !messages.find(m => m.content === accumulated)) {
        setMessages(prev => {
          if (prev[prev.length - 1]?.content === accumulated) return prev;
          return [...prev, { role: 'assistant', content: accumulated }];
        });
        setStreamContent('');
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[AISearch] Erreur:', err);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '❌ Erreur de connexion. Vérifiez que le serveur est accessible.'
        }]);
        setStreamContent('');
      }
    } finally {
      setIsStreaming(false);
      setActiveTool(null);
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleNewConversation = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setStreamContent('');
    setIsStreaming(false);
    setActiveTool(null);
    setInput('');
  };

  const hasMessages = messages.length > 0 || streamContent;

  // ─── Nom lisible des outils ─────────────────────────────────────────────────

  const toolLabel = (name) => {
    const labels = {
      rechercher_vehicule: '🔍 Recherche véhicule…',
      rechercher_client: '👤 Recherche client…',
      obtenir_statistiques: '📊 Chargement statistiques…',
      priorites_du_jour: '⚡ Analyse des priorités…',
      lister_litiges: '⚖️ Consultation litiges…',
      lister_leads: '🌐 Consultation leads…',
      lister_demandes: '💬 Consultation demandes…',
      lister_parking: '🅿️ Consultation parking…',
      planning_bilan_jour: '🔧 Analyse planning…',
      planning_rapport_cloture: '📋 Rapport de clôture…',
    };
    return labels[name] || `⏳ ${name}…`;
  };

  // ─── Rendu ─────────────────────────────────────────────────────────────────

  return (
    <div className={`ai-search ${hasMessages ? 'has-messages' : ''}`}>

      {/* État initial — landing */}
      {!hasMessages && (
        <div className="ai-search-landing">
          <div className="ai-search-icon">✨</div>
          <h1 className="ai-search-greeting">
            Bonjour {user?.prenom || user?.nom || ''}, comment puis-je vous aider ?
          </h1>

          <div className="ai-search-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                type="button"
                className="ai-search-chip"
                onClick={() => sendMessage(s.text)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Conversation */}
      {hasMessages && (
        <div className="ai-search-messages">
          {/* Bouton nouvelle conversation */}
          <div className="ai-search-new-conv">
            <button type="button" className="ai-search-new-btn" onClick={handleNewConversation}>
              + Nouvelle conversation
            </button>
          </div>

          {messages.map((m, i) => (
            <div key={i} className={`ai-search-msg ai-search-msg-${m.role}`}>
              <div className="msg-content">
                {m.role === 'assistant' ? formatMessage(m.content) : m.content}
              </div>
            </div>
          ))}

          {/* Streaming en cours */}
          {(streamContent || isStreaming) && (
            <div className="ai-search-msg ai-search-msg-assistant">
              <div className="msg-content">
                {streamContent ? formatMessage(streamContent) : null}
                {isStreaming && <span className="ai-search-cursor" />}
              </div>
              {activeTool && (
                <div className="ai-search-tool-indicator">
                  {toolLabel(activeTool)}
                </div>
              )}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Barre d'input */}
      <div className="ai-search-input-wrapper">
        <div className="ai-search-input-bar">
          <textarea
            ref={textareaRef}
            rows={1}
            placeholder="Posez votre question…"
            value={input}
            onChange={(e) => { setInput(e.target.value); adjustTextarea(); }}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
          />
          <button
            type="button"
            className="ai-search-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || isStreaming}
          >
            ➤
          </button>
        </div>
        <p className="ai-search-disclaimer">
          L'assistant peut faire des erreurs. Vérifiez les informations importantes.
        </p>
      </div>
    </div>
  );
}
